import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointerover', this.#handleCatalogIntent, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('focusin', this.#handleCatalogIntent, {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #handleCatalogIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const productLink = event.target.closest('[data-catalog-product-category]');
    if (productLink instanceof HTMLElement) {
      const menu = productLink.closest('[data-menu-layout="collection-catalog"]');
      const categoryKey = productLink.dataset.catalogProductCategory;

      if (!(menu instanceof HTMLElement) || !categoryKey) return;

      this.#updateCatalogProductPreview(menu, categoryKey, productLink);
      return;
    }

    const trigger = event.target.closest('[data-catalog-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    const menu = trigger.closest('[data-menu-layout="collection-catalog"]');
    const key = trigger.dataset.catalogTrigger;

    if (!(menu instanceof HTMLElement) || !key) return;

    this.#setActiveCatalogCategory(menu, key);
  };

  #setActiveCatalogCategory(menu, key) {
    menu.querySelectorAll('[data-catalog-trigger]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogTrigger === key);
    });

    menu.querySelectorAll('[data-catalog-panel]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogPanel === key);
    });

    menu.querySelectorAll('[data-catalog-media]').forEach((element) => {
      element.classList.toggle('is-active', element.dataset.catalogMedia === key);
    });

    this.#setActiveCatalogProduct(menu, key);
    this.#resetCatalogMedia(menu, key);
  }

  #updateCatalogProductPreview(menu, key, productLink) {
    const panel = menu.querySelector(`[data-catalog-media="${key}"]`);
    if (!(panel instanceof HTMLElement)) return;

    this.#setActiveCatalogProduct(menu, key, productLink);

    const image = panel.querySelector('[data-catalog-image-element]');
    if (!(image instanceof HTMLImageElement)) return;

    const nextSrc = productLink.dataset.catalogProductImage;
    const nextAlt = productLink.dataset.catalogProductAlt;

    if (nextSrc) {
      image.src = nextSrc;
    }

    if (nextAlt) {
      image.alt = nextAlt;
    }
  }

  #setActiveCatalogProduct(menu, key, activeLink = null) {
    menu.querySelectorAll('[data-catalog-product-category]').forEach((element) => {
      const isSameCategory = element.dataset.catalogProductCategory === key;
      const isActive =
        activeLink instanceof HTMLElement
          ? element === activeLink
          : isSameCategory &&
            element ===
              menu.querySelector(`[data-catalog-panel="${key}"] [data-catalog-product-category="${key}"]`);

      element.classList.toggle('is-active', isActive);
    });
  }

  #resetCatalogMedia(menu, key) {
    const panel = menu.querySelector(`[data-catalog-media="${key}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const image = panel.querySelector('[data-catalog-image-element]');
    if (!(image instanceof HTMLImageElement)) return;

    const defaultSrc = image.dataset.catalogDefaultSrc;
    const defaultAlt = image.dataset.catalogDefaultAlt;

    if (defaultSrc) {
      image.src = defaultSrc;
    }

    if (defaultAlt) {
      image.alt = defaultAlt;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
